export const ADMIN_PAGE_SIZE = 10;

export type UsernameMatchMode = 'contains' | 'exact';
export type DeviceStateFilter = 'active' | 'revoked' | 'reset' | 'all';

export function filterUsersByUsername<T extends { username: string }>(users: readonly T[], query: string, mode: UsernameMatchMode = 'contains'): T[] {
  const needle = query.trim().toLowerCase();
  return needle ? users.filter(user => mode === 'exact'
    ? user.username.toLowerCase() === needle
    : user.username.toLowerCase().includes(needle)) : users.slice();
}

interface DeviceStateFields {
  revokedAt?: string | null;
  resetAt?: string | null;
}

function deviceState(device: DeviceStateFields): Exclude<DeviceStateFilter, 'all'> {
  return device.resetAt ? 'reset' : device.revokedAt ? 'revoked' : 'active';
}

export function filterDevicesByState<T extends DeviceStateFields>(devices: readonly T[], state: DeviceStateFilter = 'active'): T[] {
  return state === 'all' ? devices.slice() : devices.filter(device => deviceState(device) === state);
}

export function countDeviceStates(devices: readonly DeviceStateFields[]): Record<DeviceStateFilter, number> {
  const counts = { active: 0, revoked: 0, reset: 0, all: devices.length };
  for (const device of devices) counts[deviceState(device)]++;
  return counts;
}

export interface AdminPage<T> {
  items: T[];
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  /** One-based visible range; both values are zero for an empty list. */
  start: number;
  end: number;
}

export function paginateAdminRows<T>(rows: readonly T[], requestedPage: number): AdminPage<T> {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE));
  const requested = Number.isFinite(requestedPage) ? Math.trunc(requestedPage) : 1;
  const page = Math.min(pageCount, Math.max(1, requested));
  const offset = (page - 1) * ADMIN_PAGE_SIZE;
  return {
    items: rows.slice(offset, offset + ADMIN_PAGE_SIZE), page, pageCount, total, pageSize: ADMIN_PAGE_SIZE,
    start: total ? offset + 1 : 0, end: Math.min(offset + ADMIN_PAGE_SIZE, total)
  };
}
